import React, { useState, useMemo } from 'react'

import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import ExpandedListHR from 'components/styled/ExpandedListHR'

// local components
import StatusHistoryDisplay from './components/StatusHistoryDisplay'
import Notes from './components/Notes'
import ShowMoreContent from './components/ShowMoreContent'

import useConfiguration from 'hooks/useConfiguration'
import useLetter from 'hooks/document_approval/useLetter'

function DocumentApprovalExpandedList({
  children,
  expanded,
  rowData,
  statusTitle = '',
  statusMessage = '',
  getDataDisplay,
  collapseDetails,
}) {
  const { currentModule } = useConfiguration()
  const { letter } = useLetter({
    letterId: rowData?.id,
    params: { embed: 'metadata', fields: 'id' },
  })
  const metadata = letter?._embedded?.metadata || {}
  const { notes, statusHistory } = rowData?._embedded ?? {}
  const gridWidth = notes?.length > 0 ? 12 : 6

  const [open, setOpen] = useState(false)

  const shownKeys = useMemo(() => {
    const config = currentModule?.configurations.find(
      (c) => c.configuration_type === 'module_configurations'
    )

    if (config)
      return config.settings.available_metadata_fields.map((field) =>
        field.toLowerCase()
      )
    return []
  }, [currentModule])

  return (
    <Collapse
      in={expanded}
      sx={{
        textAlign: 'left',
        pt: 2,
        mt: 0,
        '& .MuiCollapse-entered': {
          mt: 4,
        },
        bgcolor: 'background.paper',
      }}
    >
      {statusTitle && (
        <Typography fontWeight="bold" fontSize={14}>
          {statusTitle}
        </Typography>
      )}
      {statusMessage && <Box component="p">{statusMessage}</Box>}
      {statusHistory && expanded && (
        <StatusHistoryDisplay statusHistory={statusHistory} />
      )}
      <Grid container columns={gridWidth} spacing={30}>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography fontWeight="medium" fontSize={14}>
              Details
            </Typography>
            {collapseDetails && (
              <IconButton onClick={() => setOpen(!open)}>
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
          <ExpandedListHR />
          <ShowMoreContent
            grid
            rowData={metadata}
            columns={Object.keys(metadata || {}).map((key) => ({
              id: key,
              display: key,
            }))}
            visibleColumns={Object.keys(metadata || {}).filter(
              (key) => !shownKeys.includes(key.toLowerCase())
            )}
            open={open}
            getDataDisplay={getDataDisplay}
          />
        </Grid>
        {notes?.length > 0 && (
          <Grid item xs={6}>
            <Notes notes={notes} />
          </Grid>
        )}
      </Grid>
      {children}
    </Collapse>
  )
}

export default DocumentApprovalExpandedList
